import React, { useEffect, useRef, useId } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export enum ModalSize {
  Sm = "sm",
  Md = "md",
  Lg = "lg",
  Xl = "xl",
  Xxl = "2xl",
  Xxxl = "3xl",
  Fullscreen = "fullscreen",
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: ModalSize;
  "data-testid"?: string; // Made mandatory for automation testing
  preventCloseOnBackdrop?: boolean;
  preventCloseOnEsc?: boolean;
  triggerRef?: React.RefObject<HTMLElement>;
  overlayClassName?: string | undefined;
  wrapperClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
}

const sizeClasses = {
  [ModalSize.Sm]: "max-w-md",
  [ModalSize.Md]: "max-w-lg",
  [ModalSize.Lg]: "max-w-2xl",
  [ModalSize.Xl]: "max-w-4xl",
  [ModalSize.Xxl]: "max-w-6xl",
  [ModalSize.Xxxl]: "max-w-7xl",
  [ModalSize.Fullscreen]: "w-full",
};

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = ModalSize.Lg,
  "data-testid": testId = "modal",
  preventCloseOnBackdrop = false,
  preventCloseOnEsc = false,
  triggerRef,
  overlayClassName,
  wrapperClassName,
  contentClassName,
  bodyClassName,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Focus trap and lifecycle management
  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = "unset";
      triggerElementRef.current?.focus();
      triggerElementRef.current = null;
      return undefined;
    }

    // Store the element that was focused before the modal opened
    triggerElementRef.current = triggerRef?.current || (document.activeElement as HTMLElement);

    document.body.style.overflow = "hidden";

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusableElements || focusableElements.length === 0) return undefined;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Set initial focus — prefer the close button (first button with aria-label="Close modal"),
    // fall back to the first focusable element
    const closeButton = modalRef.current?.querySelector<HTMLElement>('button[data-testid$="-close-button"]');
    const focusTarget = closeButton ?? firstElement;
    const timer = setTimeout(() => focusTarget.focus(), 50);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };

    modalRef.current?.addEventListener("keydown", handleKeyDown);

    return () => {
      clearTimeout(timer);
      modalRef.current?.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
      // Restore focus on cleanup (covers both isOpen→false toggle and component unmount).
      // WCAG 2.4.3 Focus Order: if the modal unmounts while isOpen=true (e.g. route change),
      // the !isOpen branch above never runs, so focus would be lost to document.body.
      // Calling focus() here ensures focus always returns to the trigger element.
      if (triggerElementRef.current) {
        const trigger = triggerElementRef.current;
        triggerElementRef.current = null;
        requestAnimationFrame(() => { trigger.focus(); });
      }
    };
  }, [isOpen, triggerRef]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && !preventCloseOnEsc) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose, preventCloseOnEsc]);

  if (!isOpen) return null;

  const modalContent = (
    <div className={`fixed inset-0 overflow-y-auto ${overlayClassName || 'z-[250]'}`} data-testid={testId}>
      {/* Backdrop — aria-hidden so screen readers skip it; not focusable */}
      <div
        aria-hidden="true"
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={!preventCloseOnBackdrop ? (e) => { e.stopPropagation(); onClose(); } : undefined}
        data-testid={`${testId}-backdrop`}
      />

      {/* Modal Content */}
      <div className={`flex min-h-full items-center justify-center p-4 ${wrapperClassName || ""}`}>
        <div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          tabIndex={-1}
          className={`relative w-full ${sizeClasses[size]} bg-surface rounded-lg shadow-xl outline-none ${contentClassName || ""}`}
          data-testid={`${testId}-content`}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 id={titleId} className="text-xl font-medium text-text">{title}</h2>
              <button
                onClick={onClose}
                className="text-text hover:text-text transition-colors"
                data-testid={`${testId}-close-button`}
                aria-label={t('common.closeModal', 'Close modal')}
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Body */}
          <div className={`p-6 ${bodyClassName || ""}`}>{children}</div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
